import {
  type SideNavigationV2Props,
  type PageV2Component,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import { SpanCol } from "skedyul-ui/ui";
import { store } from "@store/index";
import { getWorkflowRun } from "@store/features/settings/workflowSlice";
import { open as openSideNavigation } from "@store/features/layout/sideNavigationSlice";
import { AppLayoutState } from "@/app/[subdomain]/_schema.layout";

type WorkflowRunItem = {
  id: string;
  createdAt: Date;
  startedAt: Date | null;
  completedAt: Date | null;
  failedAt: Date | null;
  cancelledAt: Date | null;
  workflow: { name: string };
  version: { versionLabel: string | null };
};

const getRunStatus = (
  run: WorkflowRunItem,
): "completed" | "failed" | "canceled" | "running" | "queued" => {
  if (run.failedAt) return "failed";
  if (run.cancelledAt) return "canceled";
  if (run.completedAt) return "completed";
  if (run.startedAt) return "running";
  return "queued";
};

const getStatusVariant = (
  status: string,
): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case "completed":
      return "default";
    case "failed":
      return "destructive";
    case "canceled":
      return "outline";
    case "running":
      return "secondary";
    case "queued":
      return "outline";
    default:
      return "secondary";
  }
};

const formatRelativeTime = (dateString: string | Date): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return "just now";
  } else if (diffMins < 60) {
    return `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  } else {
    return date.toLocaleDateString();
  }
};

export const recentWorkflowRunsSchema = (
  args: SideNavigationComponentProps<PageV2Component, { subdomain: string }>,
): SideNavigationV2Props<PageV2Component> => {
  const { sideNavigation, workflows } = args.state as AppLayoutState;
  const isOpen = sideNavigation[args.position]?.isOpen ?? true;

  const runs = (workflows?.workflowRuns?.data || []) as WorkflowRunItem[];
  const isLoading = workflows?.workflowRuns?.isLoading ?? false;
  const workflowData = workflows?.workflow?.data;

  const emptyState =
    runs.length === 0 && !isLoading
      ? [
          {
            type: "Card" as const,
            title: "No runs",
            props: {
              variant: "outline" as const,
              header: {
                title: "No workflow runs yet",
                description:
                  "Workflow runs will appear here when you execute this workflow.",
              },
            },
          },
        ]
      : [];

  const loadingState = isLoading
    ? [
        {
          type: "Card" as const,
          title: "Loading",
          props: {
            variant: "outline" as const,
            header: {
              title: "Loading runs...",
              description: "Please wait while we fetch recent workflow runs.",
            },
          },
        },
      ]
    : [];

  const dataTableComponent =
    runs.length > 0
      ? [
          {
            type: "Card" as const,
            title: "Recent Runs",
            props: {
              variant: "outline" as const,
              form: {
                formVersion: "v1" as const,
                fields: [
                  {
                    component: "DataTable" as const,
                    span: 12 as SpanCol,
                    mdSpan: 12 as SpanCol,
                    lgSpan: 12 as SpanCol,
                    props: {
                      data: runs,
                      emptyMessage: "No workflow runs found",
                      columns: [
                        {
                          id: "status",
                          type: "rowLayout" as const,
                          header: "Status",
                          accessorKey: "status",
                          align: "left" as const,
                          components: [
                            {
                              component: "Badge" as const,
                              generateProps: (
                                row: WorkflowRunItem,
                                _index: number,
                              ) => {
                                const status = getRunStatus(row);
                                return {
                                  label: status,
                                  variant: getStatusVariant(status),
                                  size: "sm" as const,
                                };
                              },
                            },
                          ],
                        },
                        {
                          id: "version",
                          type: "rowLayout" as const,
                          header: "Version",
                          accessorKey: "version",
                          align: "center" as const,
                          components: [
                            {
                              component: "Text" as const,
                              generateProps: (
                                row: WorkflowRunItem,
                                _index: number,
                              ) => ({
                                children: row.version?.versionLabel || "-",
                                className: "text-muted-foreground text-sm",
                              }),
                            },
                          ],
                        },
                        {
                          id: "timestamp",
                          type: "rowLayout" as const,
                          header: "When",
                          accessorKey: "createdAt",
                          align: "right" as const,
                          components: [
                            {
                              component: "Text" as const,
                              generateProps: (
                                row: WorkflowRunItem,
                                _index: number,
                              ) => ({
                                children: formatRelativeTime(row.createdAt),
                                className: "text-muted-foreground text-sm",
                              }),
                            },
                          ],
                        },
                      ],
                      onRowClick: async (row: WorkflowRunItem) => {
                        await store.dispatch(
                          getWorkflowRun({ workflowRunId: row.id }),
                        );

                        const { pathname, search } = window.location;
                        const params = new URLSearchParams(search);
                        params.set("run", row.id);
                        const newSearch = params.toString();
                        args.routerContext.router.push(
                          `${pathname}?${newSearch}`,
                        );

                        store.dispatch(
                          openSideNavigation({
                            position: "right",
                            schema: "WORKFLOW_RUN",
                            props: { width: "600px", mobileWidth: "80vw" },
                          }),
                        );
                      },
                    },
                  },
                ],
              },
            },
          },
        ]
      : [];

  return {
    side: args.position,
    isOpen,
    width: args.width || "400px",
    component: {
      component: "PageV2",
      props: {
        title: "Recent Runs",
        hideTitle: false,
        header: {
          icon: "History",
        },
        content: {
          structure: "sidebar",
          components: [...loadingState, ...emptyState, ...dataTableComponent],
        },
      },
    },
  };
};
